import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	Avatar,
	CircularProgress,
	IconButton,
	Tooltip,
	Button,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Pagination,
} from "@mui/material";
import {
	ArrowLeft,
	Search,
	Filter,
	Mail,
	MailOpen,
	User,
	Calendar,
	MessageSquare,
	CalendarDays,
	Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SendNotificationModal from "../../components/notifications/SendNotificationModal";

// Component to display school name
const SchoolName = ({ schoolId }) => {
	const { supabase } = useAuth();
	const [schoolName, setSchoolName] = useState("");
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchSchoolName = async () => {
			try {
				const { data, error } = await supabase
					.from("schools")
					.select("name")
					.eq("id", schoolId)
					.single();

				if (error) throw error;
				setSchoolName(data?.name || "Unknown School");
			} catch (error) {
				setSchoolName("Unknown School");
			} finally {
				setLoading(false);
			}
		};

		if (schoolId) {
			fetchSchoolName();
		} else {
			setLoading(false);
		}
	}, [schoolId, supabase]);

	if (loading) {
		return <CircularProgress size={16} />;
	}

	return (
		<Typography variant="body2" color="text.secondary">
			{schoolName}
		</Typography>
	);
};

const AllNotifications = () => {
	const { supabase, user } = useAuth();
	const navigate = useNavigate();
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterType, setFilterType] = useState("all");
	const [filterReadStatus, setFilterReadStatus] = useState("all");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [userProfile, setUserProfile] = useState(null);
	const [userSchools, setUserSchools] = useState([]);
	const [openSendModal, setOpenSendModal] = useState(false);
	const [profileLoaded, setProfileLoaded] = useState(false);
	const itemsPerPage = 10;

	useEffect(() => {
		if (user?.id) {
			fetchUserProfile();
		}
	}, [user?.id]);

	useEffect(() => {
		if (profileLoaded && userProfile) {
			fetchNotifications();
		}
	}, [
		profileLoaded,
		userProfile,
		userSchools,
		currentPage,
		searchTerm,
		filterType,
		filterReadStatus,
		dateFrom,
		dateTo,
	]);

	const fetchUserProfile = async () => {
		try {
			if (!user?.id) return;

			// Fetch user profile with role
			const { data: profile, error: profileError } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", user.id)
				.single();

			if (profileError) throw profileError;
			setUserProfile(profile);

			// If user is admin or employee, fetch their associated schools
			if (
				profile?.role === "ROLE_FUNMATH_ADMIN" ||
				profile?.role === "ROLE_FUNMIYO_EMPLOYEE"
			) {
				const { data: schools, error: schoolsError } = await supabase
					.from("employee_schools")
					.select("school_id")
					.eq("profile_id", user.id)
					.eq("active_status", true);

				if (schoolsError) throw schoolsError;
				const schoolIds = schools?.map((s) => s.school_id) || [];
				setUserSchools(schoolIds);
			} else {
				// For non-admin users, clear schools array
				setUserSchools([]);
			}
		} catch (error) {
			console.error("Error fetching user profile:", error);
		} finally {
			setProfileLoaded(true);
		}
	};

	const fetchNotifications = async () => {
		try {
			setLoading(true);

			let allowedRecipientIds = null;

			// Get allowed recipient IDs for role-based filtering
			if (
				userProfile?.role === "ROLE_FUNMATH_ADMIN" ||
				userProfile?.role === "ROLE_FUNMIYO_EMPLOYEE"
			) {
				if (userSchools.length > 0) {
					const { data: recipients, error: recipientsError } = await supabase
						.from("profiles")
						.select("id")
						.in("school_id", userSchools);

					if (recipientsError) throw recipientsError;
					allowedRecipientIds = recipients?.map((p) => p.id) || [];

					if (allowedRecipientIds.length === 0) {
						setNotifications([]);
						setTotalCount(0);
						setLoading(false);
						return;
					}
				} else {
					// If admin/employee but no schools assigned, show no notifications
					setNotifications([]);
					setTotalCount(0);
					setLoading(false);
					return;
				}
			}

			// Build the main query
			let query = supabase
				.from("notifications")
				.select(
					`
					*,
					sender:sender_id (
						full_name,
						email,
						avatar_url
					),
					recipient:recipient_id (
						full_name,
						email,
						avatar_url,
						school_id
					)
				`
				)
				.order("created_at", { ascending: false });

			// Apply role-based filtering
			if (allowedRecipientIds) {
				query = query.in("recipient_id", allowedRecipientIds);
			}

			// Apply other filters
			if (searchTerm) {
				query = query.or(
					`subject.ilike.%${searchTerm}%,message.ilike.%${searchTerm}%`
				);
			}

			if (filterType !== "all") {
				query = query.eq("type", filterType);
			}

			if (filterReadStatus !== "all") {
				query = query.eq("is_read", filterReadStatus === "read");
			}

			// Apply date filters
			if (dateFrom) {
				query = query.gte("created_at", `${dateFrom}T00:00:00.000Z`);
			}

			if (dateTo) {
				query = query.lte("created_at", `${dateTo}T23:59:59.999Z`);
			}

			// Get total count for pagination (with same filters)
			let countQuery = supabase
				.from("notifications")
				.select("*", { count: "exact", head: true });

			// Apply role-based filtering to count query
			if (allowedRecipientIds) {
				countQuery = countQuery.in("recipient_id", allowedRecipientIds);
			}

			if (searchTerm) {
				countQuery = countQuery.or(
					`subject.ilike.%${searchTerm}%,message.ilike.%${searchTerm}%`
				);
			}

			if (filterType !== "all") {
				countQuery = countQuery.eq("type", filterType);
			}

			if (filterReadStatus !== "all") {
				countQuery = countQuery.eq("is_read", filterReadStatus === "read");
			}

			if (dateFrom) {
				countQuery = countQuery.gte("created_at", `${dateFrom}T00:00:00.000Z`);
			}

			if (dateTo) {
				countQuery = countQuery.lte("created_at", `${dateTo}T23:59:59.999Z`);
			}

			const { count } = await countQuery;
			setTotalCount(count || 0);

			// Apply pagination
			const start = (currentPage - 1) * itemsPerPage;
			const end = start + itemsPerPage - 1;
			query = query.range(start, end);

			const { data, error } = await query;

			if (error) throw error;
			setNotifications(data || []);
		} catch (error) {
			console.error("Error fetching notifications:", error);
		} finally {
			setLoading(false);
		}
	};

	const markAsRead = async (notificationId) => {
		try {
			const { error } = await supabase
				.from("notifications")
				.update({
					is_read: true,
					read_at: new Date().toISOString(),
				})
				.eq("id", notificationId);

			if (error) throw error;

			// Update local state
			setNotifications((prev) =>
				prev.map((notif) =>
					notif.id === notificationId
						? { ...notif, is_read: true, read_at: new Date().toISOString() }
						: notif
				)
			);
		} catch (error) {
			console.error("Error marking notification as read:", error);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return "Not read";
		return new Date(dateString).toLocaleString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getNotificationTypeColor = (type) => {
		switch (type) {
			case "admin_message":
				return "primary";
			case "game_result_edited":
				return "success";
			case "system_alert":
				return "warning";
			case "reminder":
				return "info";
			case "announcement":
				return "success";
			default:
				return "default";
		}
	};

	const clearAllFilters = () => {
		setSearchTerm("");
		setFilterType("all");
		setFilterReadStatus("all");
		setDateFrom("");
		setDateTo("");
		setCurrentPage(1);
	};

	const setQuickDateFilter = (days) => {
		const today = new Date();
		const fromDate = new Date(today);
		fromDate.setDate(today.getDate() - days);

		setDateFrom(fromDate.toISOString().split("T")[0]);
		setDateTo(today.toISOString().split("T")[0]);
		setCurrentPage(1);
	};

	const totalPages = Math.ceil(totalCount / itemsPerPage);

	// Check if user can send notifications (admin or employee)
	const canSendNotifications =
		userProfile?.role === "ROLE_FUNMATH_ADMIN" ||
		userProfile?.role === "ROLE_FUNMIYO_EMPLOYEE";

	// Show loading while fetching user profile or notifications
	if (loading || !profileLoaded) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
				<CircularProgress />
			</Box>
		);
	}

	// Show message if admin/employee has no schools assigned
	if (
		(userProfile?.role === "ROLE_FUNMATH_ADMIN" ||
			userProfile?.role === "ROLE_FUNMIYO_EMPLOYEE") &&
		userSchools.length === 0
	) {
		return (
			<Box sx={{ p: 3, maxWidth: "1400px", mx: "auto" }}>
				<Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
					<IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
						<ArrowLeft size={20} />
					</IconButton>
					<Typography variant="h4" sx={{ fontWeight: "bold", flexGrow: 1 }}>
						All Notifications
					</Typography>
				</Box>
				<Paper sx={{ p: 4, textAlign: "center" }}>
					<MessageSquare size={48} color="#ccc" />
					<Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
						No Schools Assigned
					</Typography>
					<Typography variant="body2" color="text.secondary">
						You don't have any schools assigned to view notifications for.
					</Typography>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, maxWidth: "1400px", mx: "auto" }}>
			{/* Header */}
			<Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
				<IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
					<ArrowLeft size={20} />
				</IconButton>
				<Typography variant="h4" sx={{ fontWeight: "bold", flexGrow: 1 }}>
					All Notifications
					{(userProfile?.role === "ROLE_FUNMATH_ADMIN" ||
						userProfile?.role === "ROLE_FUNMIYO_EMPLOYEE") && (
						<Typography
							variant="caption"
							sx={{
								display: "block",
								fontWeight: "normal",
								color: "text.secondary",
							}}>
							Showing notifications for users in your assigned schools
						</Typography>
					)}
				</Typography>

				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					{canSendNotifications && (
						<Button
							variant="contained"
							startIcon={<Send size={20} />}
							onClick={() => setOpenSendModal(true)}
							sx={{
								bgcolor: "primary.main",
								"&:hover": {
									bgcolor: "primary.dark",
								},
							}}>
							Send Notification
						</Button>
					)}
					<Typography variant="body2" color="text.secondary">
						{totalCount} total notifications
					</Typography>
				</Box>
			</Box>

			{/* Filters */}
			<Paper sx={{ p: 3, mb: 3 }}>
				<Box
					sx={{
						display: "flex",
						gap: 2,
						flexWrap: "wrap",
						alignItems: "center",
						mb: 2,
					}}>
					<TextField
						placeholder="Search notifications..."
						variant="outlined"
						size="small"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						sx={{ minWidth: 250 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search size={20} />
								</InputAdornment>
							),
						}}
					/>

					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel>Type</InputLabel>
						<Select
							value={filterType}
							label="Type"
							onChange={(e) => setFilterType(e.target.value)}>
							<MenuItem value="all">All Types</MenuItem>
							<MenuItem value="admin_message">Admin Message</MenuItem>
							<MenuItem value="game_result_edited">Edited Video</MenuItem>
							<MenuItem value="system_alert">System Alert</MenuItem>
							<MenuItem value="reminder">Reminder</MenuItem>
							<MenuItem value="announcement">Announcement</MenuItem>
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 150 }}>
						<InputLabel>Status</InputLabel>
						<Select
							value={filterReadStatus}
							label="Status"
							onChange={(e) => setFilterReadStatus(e.target.value)}>
							<MenuItem value="all">All Status</MenuItem>
							<MenuItem value="unread">Unread</MenuItem>
							<MenuItem value="read">Read</MenuItem>
						</Select>
					</FormControl>

					<Button
						variant="outlined"
						startIcon={<Filter size={16} />}
						onClick={clearAllFilters}>
						Clear All Filters
					</Button>
				</Box>

				{/* Date Filters Row */}
				<Box
					sx={{
						display: "flex",
						gap: 2,
						flexWrap: "wrap",
						alignItems: "center",
						pt: 2,
						borderTop: "1px solid #e5e7eb",
					}}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<CalendarDays size={16} color="#6b7280" />
						<Typography variant="body2" color="text.secondary">
							Date Range:
						</Typography>
					</Box>

					<TextField
						label="From Date"
						type="date"
						size="small"
						value={dateFrom}
						onChange={(e) => setDateFrom(e.target.value)}
						InputLabelProps={{
							shrink: true,
						}}
						sx={{ minWidth: 150 }}
					/>

					<TextField
						label="To Date"
						type="date"
						size="small"
						value={dateTo}
						onChange={(e) => setDateTo(e.target.value)}
						InputLabelProps={{
							shrink: true,
						}}
						sx={{ minWidth: 150 }}
					/>

					{/* Quick Date Filter Buttons */}
					<Box sx={{ display: "flex", gap: 1, ml: 2 }}>
						<Button
							size="small"
							variant="outlined"
							onClick={() => setQuickDateFilter(7)}
							sx={{ fontSize: "0.75rem", py: 0.5 }}>
							Last 7 Days
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={() => setQuickDateFilter(30)}
							sx={{ fontSize: "0.75rem", py: 0.5 }}>
							Last 30 Days
						</Button>
						<Button
							size="small"
							variant="outlined"
							onClick={() => setQuickDateFilter(90)}
							sx={{ fontSize: "0.75rem", py: 0.5 }}>
							Last 90 Days
						</Button>
					</Box>
				</Box>

				{/* Active Filters Display */}
				{(searchTerm ||
					filterType !== "all" ||
					filterReadStatus !== "all" ||
					dateFrom ||
					dateTo) && (
					<Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5e7eb" }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ mb: 1, display: "block" }}>
							Active Filters:
						</Typography>
						<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
							{searchTerm && (
								<Chip
									label={`Search: "${searchTerm}"`}
									size="small"
									onDelete={() => setSearchTerm("")}
									color="primary"
									variant="outlined"
								/>
							)}
							{filterType !== "all" && (
								<Chip
									label={`Type: ${filterType.replace("_", " ")}`}
									size="small"
									onDelete={() => setFilterType("all")}
									color="primary"
									variant="outlined"
								/>
							)}
							{filterReadStatus !== "all" && (
								<Chip
									label={`Status: ${filterReadStatus}`}
									size="small"
									onDelete={() => setFilterReadStatus("all")}
									color="primary"
									variant="outlined"
								/>
							)}
							{(dateFrom || dateTo) && (
								<Chip
									label={`Date: ${dateFrom || "..."} to ${dateTo || "..."}`}
									size="small"
									onDelete={() => {
										setDateFrom("");
										setDateTo("");
									}}
									color="primary"
									variant="outlined"
								/>
							)}
						</Box>
					</Box>
				)}
			</Paper>

			{/* Notifications Table */}
			<TableContainer component={Paper} elevation={2}>
				<Table>
					<TableHead>
						<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
							<TableCell>Status</TableCell>
							<TableCell>Subject</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Recipient</TableCell>
							<TableCell>School</TableCell>
							<TableCell>Sent Date</TableCell>
							<TableCell>Read Date</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{notifications.length === 0 ? (
							<TableRow>
								<TableCell colSpan={7} align="center" sx={{ py: 6 }}>
									<MessageSquare size={48} color="#ccc" />
									<Typography
										variant="h6"
										color="text.secondary"
										sx={{ mt: 2 }}>
										No notifications found
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Try adjusting your search or filter criteria
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							notifications.map((notification) => (
								<TableRow
									key={notification.id}
									hover
									sx={{
										backgroundColor: notification.is_read
											? "transparent"
											: "#f8f9fa",
										"&:hover": { backgroundColor: "#f5f5f5" },
									}}>
									<TableCell>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											{notification.is_read ? (
												<MailOpen size={16} color="#6b7280" />
											) : (
												<Mail size={16} color="#3b82f6" />
											)}
											<Chip
												label={notification.is_read ? "Read" : "Unread"}
												size="small"
												color={notification.is_read ? "default" : "primary"}
												variant="outlined"
											/>
										</Box>
									</TableCell>

									<TableCell>
										<Box>
											<Typography
												variant="body2"
												sx={{
													fontWeight: notification.is_read ? "normal" : "bold",
													mb: 0.5,
												}}>
												{notification.subject}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													display: "-webkit-box",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													overflow: "hidden",
													textOverflow: "ellipsis",
													maxWidth: 300,
												}}>
												{notification.message}
											</Typography>
										</Box>
									</TableCell>

									<TableCell>
										<Chip
											label={notification.type.replace("_", " ").toUpperCase()}
											size="small"
											color={getNotificationTypeColor(notification.type)}
											variant="outlined"
										/>
									</TableCell>

									<TableCell>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Avatar
												src={notification.recipient?.avatar_url}
												sx={{ width: 32, height: 32 }}>
												<User size={16} />
											</Avatar>
											<Box>
												<Typography
													variant="body2"
													sx={{ fontWeight: "medium" }}>
													{notification.recipient?.full_name || "Unknown"}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{notification.recipient?.email || "No email"}
												</Typography>
											</Box>
										</Box>
									</TableCell>

									<TableCell>
										{notification.recipient?.school_id ? (
											<SchoolName schoolId={notification.recipient.school_id} />
										) : (
											<Typography variant="body2" color="text.secondary">
												No school assigned
											</Typography>
										)}
									</TableCell>

									<TableCell>{formatDate(notification.created_at)}</TableCell>

									<TableCell>{formatDate(notification.read_at)}</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pagination */}
			{totalPages > 1 && (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={(event, value) => setCurrentPage(value)}
						color="primary"
						size="large"
					/>
				</Box>
			)}

			{/* Send Notification Modal */}
			{userProfile && (
				<SendNotificationModal
					open={openSendModal}
					onClose={() => setOpenSendModal(false)}
					supabase={supabase}
					currentUser={userProfile}
					userSchoolIds={userSchools}
					onNotificationSent={() => {
						// Refresh notifications when a new one is sent
						if (userProfile) {
							fetchNotifications();
						}
					}}
				/>
			)}
		</Box>
	);
};

export default AllNotifications;
